import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { get, post } from "@/lib/api";
import { date, eur, pct } from "@/lib/format";
import type { UnbilledOutput } from "@/lib/types";

export function Verrechnung() {
  const [sp, setSp] = useSearchParams();
  const client = Number(sp.get("client")) || null;
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ["unbilled", client],
    queryFn: () => get<UnbilledOutput>(`/api/expense-shares?unbilled=1${client ? `&client=${client}` : ""}`),
  });
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [ref, setRef] = useState("");

  const stamp = useMutation({
    mutationFn: () => post("/api/expense-shares/invoiced", { shareIds: [...picked], rebillRef: ref.trim() }),
    onSuccess: () => {
      toast.success(`${picked.size} Anteil(e) als ${ref.trim()} verrechnet`);
      setPicked(new Set());
      setRef("");
      qc.invalidateQueries({ queryKey: ["unbilled"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (e) => toast.error(e.message),
  });
  const toggle = (id: number) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const shares = data.data?.shares ?? [];
  const pickedSum = shares.filter((s) => picked.has(s.id)).reduce((t, s) => t + s.rebill_amount, 0);

  return (
    <div className="space-y-5">
      <PageHeader title="Verrechnung" parent={{ to: "/ausgaben", label: "Ausgaben" }} />
      <p className="max-w-prose text-sm text-muted-foreground">Kunden-Anteile ohne Rechnungs-Nr., je Kunde. Kunde wählen, Anteile markieren, Rechnungs-Nr. eintragen.</p>
      <Table className="table-fixed">
        <colgroup><col /><col style={{ width: "5.5rem" }} /><col style={{ width: "7.5rem" }} /><col style={{ width: "9rem" }} /><col className="max-sm:hidden" style={{ width: "13rem" }} /><col style={{ width: "2.5rem" }} /></colgroup>
        <TableHeader>
          <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
            <TableHead>Kunde</TableHead><TableHead className="text-right">Anteile</TableHead><TableHead className="text-right">Kosten</TableHead>
            <TableHead className="text-right">Weiterverrechnen</TableHead><TableHead className="max-sm:hidden">Zeitraum</TableHead><TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.isPending ? <SkeletonRows columns={6} rows={3} /> : (data.data?.summary.length ?? 0) === 0 ? (
            <EmptyRow columns={6}>Nichts offen – alle Kunden-Anteile haben eine Rechnungs-Nr.</EmptyRow>
          ) : data.data?.summary.map((s) => {
            const pick = () => { setPicked(new Set()); setSp({ client: String(s.client_id) }, { replace: true }); };
            return (
              <TableRow key={s.client_id} data-state={client === s.client_id ? "selected" : undefined} tabIndex={0}
                aria-label={`Anteile von ${s.client_name} anzeigen`}
                className="group h-12 cursor-pointer outline-none focus-visible:bg-muted/60"
                onClick={pick} onKeyDown={(e) => { if (e.key === "Enter") pick(); }}>
                <TableCell className="overflow-hidden font-medium"><span className="block truncate">{s.client_name}</span></TableCell>
                <TableCell className="text-right tabular-nums">{s.count}</TableCell>
                <TableCell className="text-right tabular-nums">{eur(s.cost_eur)}</TableCell>
                <TableCell className="text-right tabular-nums">{eur(s.rebill_eur)}</TableCell>
                <TableCell className="tabular-nums text-muted-foreground max-sm:hidden">{date(s.from)} – {date(s.to)}</TableCell>
                <TableCell className="text-muted-foreground/60 group-hover:text-foreground"><ChevronRight aria-hidden className="size-4" /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {client && shares.length > 0 && (
        <section className="space-y-3" aria-labelledby="shares-title">
          <h2 id="shares-title" className="text-base font-semibold">Anteile von {shares[0].client_name}</h2>
          <Table className="table-fixed">
            <colgroup><col style={{ width: "2.75rem" }} /><col style={{ width: "6.5rem" }} /><col /><col style={{ width: "7rem" }} /><col style={{ width: "5rem" }} /><col style={{ width: "9rem" }} /></colgroup>
            <TableHeader>
              <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
                <TableHead>
                  <label className="-my-2 flex h-10 cursor-pointer items-center">
                    <Checkbox aria-label="Alle auswählen"
                      checked={picked.size === shares.length ? true : picked.size > 0 ? "indeterminate" : false}
                      onCheckedChange={(v) => setPicked(v ? new Set(shares.map((x) => x.id)) : new Set())} />
                  </label>
                </TableHead>
                <TableHead>Datum</TableHead><TableHead>Empfänger</TableHead><TableHead className="text-right">Anteil</TableHead>
                <TableHead className="text-right">Marge</TableHead><TableHead className="text-right">Weiterverrechnen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shares.map((s) => (
                <TableRow key={s.id} data-state={picked.has(s.id) ? "selected" : undefined} className="h-11 cursor-pointer" onClick={() => toggle(s.id)}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <label className="-my-2 flex h-11 cursor-pointer items-center">
                      <Checkbox aria-label="Auswählen" checked={picked.has(s.id)} onCheckedChange={() => toggle(s.id)} />
                    </label>
                  </TableCell>
                  <TableCell className="tabular-nums">{date(s.value_date)}</TableCell>
                  <TableCell className="overflow-hidden"><span className="block truncate" title={s.payee}>{s.payee}</span></TableCell>
                  <TableCell className="text-right tabular-nums">{eur(s.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{pct(s.margin_pct)}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(s.rebill_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); if (picked.size > 0 && ref.trim() && !stamp.isPending) stamp.mutate(); }}
          >
            <Input className="w-40" aria-label="Rechnungs-Nr." placeholder="Rechnungs-Nr." autoComplete="off" spellCheck={false} data-1p-ignore
              value={ref} onChange={(e) => setRef(e.target.value)} />
            <Button type="submit" disabled={picked.size === 0 || !ref.trim() || stamp.isPending}>Als verrechnet markieren</Button>
            <span className="text-sm text-muted-foreground tabular-nums">
              {picked.size > 0 ? `${picked.size} gewählt · ${eur(pickedSum)}` : "Noch nichts gewählt"}
            </span>
          </form>
        </section>
      )}
    </div>
  );
}
